import React from 'react';

import { Box } from '@waglabs/box-next';
import { Flex } from '@waglabs/flex-next';
import { LazyImage } from '@waglabs/lazyload';
import Link from '@waglabs/link';
import { Typography } from '@waglabs/typography';

import { ServiceUrls } from '../../../../constants';
import { isEven } from '../../../../utils';
import {
  ServiceBoardingCircleIcon,
  ServiceDropInCircleIcon,
  ServiceHealthCircleIcon,
  ServiceOnDemandWalkCircleIcon,
  ServicePremiumIcon,
  ServiceRecurringWalkCircleIcon,
  ServiceSittingCircleIcon,
  ServiceTrainingCircleIcon,
} from '../../../../components/LocalIcons';

import boarding2xImage from '../../../../assets/services/service-boarding-desktop-2x.jpg';
import boarding3xImage from '../../../../assets/services/service-boarding-desktop-3x.jpg';
import dropIn2xImage from '../../../../assets/services/service-drop-ins-desktop-2x.jpg';
import dropIn3xImage from '../../../../assets/services/service-drop-ins-desktop-3x.jpg';
import health2xImage from '../../../../assets/services/service-health-desktop-2x.jpg';
import health3xImage from '../../../../assets/services/service-health-desktop-3x.jpg';
import premium2xImage from '../../../../assets/services/service-premium-2x.png';
import premium3xImage from '../../../../assets/services/service-premium-3x.png';
import recurring2xImage from '../../../../assets/services/service-recurring-2x.png';
import recurring3xImage from '../../../../assets/services/service-recurring-3x.png';
import sitting2xImage from '../../../../assets/services/service-sitting-desktop-2x.jpg';
import sitting3xImage from '../../../../assets/services/service-sitting-desktop-3x.jpg';
import training2xImage from '../../../../assets/services/service-training-desktop-2x.jpg';
import training3xImage from '../../../../assets/services/service-training-desktop-3x.jpg';
import walk2xImage from '../../../../assets/services/service-walk-desktop-2x.jpg';
import walk3xImage from '../../../../assets/services/service-walk-desktop-3x.jpg';
import wellness2xImage from '../../../../assets/services/service-wellness-desktop-2x.jpg';
import wellness3xImage from '../../../../assets/services/service-wellness-desktop-3x.jpg';
import petted2xImage from '../../../../assets/services/service-petted-desktop-2x.jpg';
import petted3xImage from '../../../../assets/services/service-petted-desktop-3x.jpg';
import dfa2xImage from '../../../../assets/services/service-dfa-desktop-2x.jpg';
import dfa3xImage from '../../../../assets/services/service-dfa-desktop-3x.jpg';
import cfa2xImage from '../../../../assets/services/service-cfa-desktop-2x.jpg';
import maxbone2xImage from '../../../../assets/services/service-maxbone-desktop@2x.jpg';
import maxbone3xImage from '../../../../assets/services/service-maxbone-desktop@3x.jpg';
import maxboneMobile2xImage from '../../../../assets/services/service-maxbone-mobile@2x.jpg';
import maxboneMobile3xImage from '../../../../assets/services/service-maxbone-mobile@3x.jpg';

import leash2xSidePhotoImage from '../../../../assets/services/side-photos/leash-2x.jpg';
import tugToy2xSidePhotoImage from '../../../../assets/services/side-photos/tug-toy-2x.jpg';
import reats2xSidePhotoImage from '../../../../assets/services/side-photos/reats-2x.jpg';
import foodBowl2xSidePhotoImage from '../../../../assets/services/side-photos/food-bowl-2x.jpg';
import stethoscope2xSidePhotoImage from '../../../../assets/services/side-photos/stethoscope-2x.jpg';
import wagExpandableBowl2xSidePhotoImage from '../../../../assets/services/side-photos/wag-expandable-bowl-2x.png';
import catToy2x from '../../../../assets/services/side-photos/cat-toy 2x.png';
import catToy3x from '../../../../assets/services/side-photos/cat-toy 3x.png';
import wagBall2x from '../../../../assets/services/side-photos/wag-ball-2x.png';

import pettedLogo from '../../../../assets/services/petted-logo.svg';
import wagWellnessLogo from '../../../../assets/services/wag-wellness-logo.svg';
import dfaLogo from '../../../../assets/services/dfa-logo.png';
import cfaLogo from '../../../../assets/services/cfaLogo.svg';
import maxboneLogo from '../../../../assets/services/maxbone_logo.svg';

const services = [
  {
    name: 'On-demand Dog Walking',
    url: ServiceUrls.Walking,
    IconComponent: ServiceOnDemandWalkCircleIcon,
    src: walk2xImage,
    srcSet: `${walk2xImage} 2x, ${walk3xImage} 3x`,
    description: (
      <Typography>
        Instantly book trusted, local, 5-star dog walkers in your neighborhood,
        then track the walk via GPS.
      </Typography>
    ),
    sidePhoto: (
      <>
        <LazyImage
          src={leash2xSidePhotoImage}
          alt="side photo leash"
          width={['100px', null, '216px']}
          height={['140px', null, '300px']}
          position="absolute"
          right={['-44px', null, 0]}
          top={['-30px', null, '-180px']}
        />
      </>
    ),
  },
  {
    name: 'Recurring Dog Walks',
    url: ServiceUrls.Walking,
    IconComponent: ServiceRecurringWalkCircleIcon,
    src: recurring2xImage,
    srcSet: `${recurring2xImage} 2x, ${recurring3xImage} 3x`,
    description: (
      <Typography>
        {'Select the dates and times that best suit your pup\'s needs for worry-free pet care.'}
      </Typography>
    ),
    sidePhoto: (
      <>
        <LazyImage
          src={tugToy2xSidePhotoImage}
          alt="side photo tug toy"
          width={['80px', null, '216px']}
          height={['100px', null, '300px']}
          position="absolute"
          left={[0]}
          top={['-40px', null, '-100px']}
        />
      </>
    ),
  },
  {
    name: 'Wag! Premium',
    url: ServiceUrls.Premium,
    IconComponent: ServicePremiumIcon,
    src: premium2xImage,
    srcset: `${premium2xImage} 2x, ${premium3xImage} 3x`,
    description: (
      <Typography>
        Enjoy zero service fees, discounts on all pet care services, and FREE Vet Chat.
        Plus, get VIP customer care service.
      </Typography>
    ),
    sidePhoto: (
      <>
        <LazyImage
          src={reats2xSidePhotoImage}
          alt="side photo treats"
          width={['130px', null, '250px']}
          height={['150px', null, '300px']}
          position="absolute"
          right={0}
          top={['-40px', null, '-100px']}
        />
      </>
    ),
  },
  {
    name: 'Drop-In',
    url: ServiceUrls.DropIn,
    IconComponent: ServiceDropInCircleIcon,
    src: dropIn2xImage,
    srcSet: `${dropIn2xImage} 2x, ${dropIn3xImage} 3x`,
    description: (
      <>
        <Typography>
          A quick home visit for dogs and cats who love having friends over.
        </Typography>
      </>
    ),
  },
  {
    name: 'Training',
    url: ServiceUrls.Training,
    IconComponent: ServiceTrainingCircleIcon,
    src: training2xImage,
    srcSet: `${training2xImage} 2x, ${training3xImage} 3x`,
    description: (
      <Typography>
        One-on-one in-person training sessions.
      </Typography>
    ),
    sidePhoto: (
      <>
        <LazyImage
          src={foodBowl2xSidePhotoImage}
          alt="side photo food bowl"
          width={['80px', null, '164px']}
          height={['210px', null, '400px']}
          position="absolute"
          left={['-24px', null, 0]}
          top={['380px', null, '288px']}
          zIndex={0}
          pointerEvents="none"
        />
      </>
    ),
  },
  {
    name: 'Boarding',
    url: ServiceUrls.Boarding,
    IconComponent: ServiceBoardingCircleIcon,
    src: boarding2xImage,
    srcSet: `${boarding2xImage} 2x, ${boarding3xImage} 3x`,
    description: (
      <Typography>
        A sleepover for your pet in a Pet Caregivers home.
      </Typography>
    ),
    sidePhoto: null,
  },
  {
    name: 'Sitting',
    url: ServiceUrls.Sitting,
    IconComponent: ServiceSittingCircleIcon,
    src: sitting2xImage,
    srcSet: `${sitting2xImage} 2x, ${sitting3xImage} 3x`,
    description: (
      <Typography>
        A sleepover for your pet in the comfort of your own home.
      </Typography>
    ),
  },
  {
    name: 'Vet Chat',
    IconComponent: ServiceHealthCircleIcon,
    src: health2xImage,
    srcSet: `${health2xImage} 2x, ${health3xImage} 3x`,
    description: (
      <Typography>
        Chat with a licensed pet expert 24/7.
      </Typography>
    ),
    sidePhoto: null,
  },
  {
    name: 'Pet Insurer',
    nameBlock: (
      <>
        <LazyImage
          src={pettedLogo}
          alt="Pet Insurer logo"
          width="208px"
          height="48px"
        />
      </>
    ),
    url: ServiceUrls.Petted,
    IconComponent: () => null,
    src: wellness2xImage,
    srcSet: `${wellness2xImage} 2x, ${wellness3xImage} 3x`,
    description: (
      <Typography>
        Compare and purchase
        <Link
          href={ServiceUrls.PetInsurer}
          target="_blank"
          color="wagGreen"
          fontWeight="bold"
        >
          &nbsp;top-rated pet insurance plans&nbsp;
        </Link>
        with a simple click.
      </Typography>
    ),
    sidePhoto: (
      <>
        <LazyImage
          src={stethoscope2xSidePhotoImage}
          alt="side photo stethoscope"
          width={['80px', null, '200px']}
          height={['210px', null, '400px']}
          position="absolute"
          right={0}
          top={['-72px', null, '-240px']}
        />
      </>
    ),
  },
  {
    name: 'Wag! Wellness Plans',
    nameBlock: (
      <>
        <LazyImage
          alt="Wag! Wellness logo"
          src={wagWellnessLogo}
          width="208px"
          height="48px"
        />
      </>
    ),
    url: ServiceUrls.WellnessPlans,
    IconComponent: () => null,
    src: petted2xImage,
    srcSet: `${petted2xImage} 2x, ${petted3xImage} 3x`,
    sidePhoto: (
      <>
        <LazyImage
          src={wagExpandableBowl2xSidePhotoImage}
          alt="side photo Wag! expandable bowl"
          width={['64px', null, '280px']}
          height={['168px', null, '280px']}
          position="absolute"
          left={[0, null, '-70px']}
          top={['-147px', null, '-240px']}
          imageProps={{
            style: {
              objectFit: 'contain',
            },
          }}
        />
      </>
    ),
    description: (
      <Typography>
        Simple and affordable
        <Link
          href={ServiceUrls.WellnessPlans}
          target="_blank"
          color="wagGreen"
          fontWeight="bold"
        >
          &nbsp;routine wellness plans&nbsp;
        </Link>
        to cover vaccines, flea & tick, meds, and more.
      </Typography>
    ),
  },
  {
    name: 'Maxbone',
    nameBlock: (
      <>
        <LazyImage
          alt="maxbone logo"
          src={maxboneLogo}
          width="208px"
          height="48px"
          imageProps={{
            style: {
              objectFit: 'contain',
            },
          }}
        />
      </>
    ),
    url: ServiceUrls.Maxbone,
    IconComponent: () => null,
    src: maxbone2xImage,
    srcSet: `${maxbone2xImage} 2x, ${maxbone3xImage} 3x`,
    responsive: {
      mobile: {
        src: maxboneMobile2xImage,
        srcSet: `${maxboneMobile2xImage} 2x, ${maxboneMobile3xImage} 3x`,
      },
      desktop: {
        src: maxbone2xImage,
        srcSet: `${maxbone2xImage} 2x, ${maxbone3xImage} 3x`,
      },
    },
    sidePhoto: (
      <>
        <LazyImage
          src={wagBall2x}
          alt="side photo Wag! ball"
          width={['80px', null, '220px']}
          height={['210px', null, '220px']}
          position="absolute"
          right={0}
          top={['-72px', null, '-240px']}
          imageProps={{
            style: {
              objectFit: 'contain',
            },
          }}
        />
      </>
    ),
    description: (
      <Typography>
        <Link
          href={ServiceUrls.Maxbone}
          target="_blank"
          color="wagGreen"
          fontWeight="bold"
        >
          Maxbone
        </Link>
        {' is the one-stop-shop for premium pet products, enabling furry family members to look and feel good through premium essentials delivered straight to the front door.'}
      </Typography>
    ),
  },
  {
    name: 'DogFoodAdvisor',
    nameBlock: (
      <>
        <LazyImage
          alt="Dog Food Advisor logo"
          src={dfaLogo}
          width="224px"
          height="48px"
        />
      </>
    ),
    url: ServiceUrls.DogFoodAdvisor,
    IconComponent: () => null,
    src: dfa2xImage,
    srcSet: `${dfa2xImage} 2x, ${dfa3xImage} 3x`,
    description: (
      <Typography>
        Search and find
        <Link
          href={ServiceUrls.DogFoodAdvisor}
          target="_blank"
          color="wagGreen"
          fontWeight="bold"
        >
          &nbsp;the best dog food&nbsp;
        </Link>
        for your dog based on unbiased dog food
        reviews and ratings by brand.
      </Typography>
    ),
  },
  {
    name: 'CatFoodAdvisor',
    nameBlock: (
      <>
        <LazyImage
          alt="Cat Food Advisor logo"
          src={cfaLogo}
          width="224px"
          height="48px"
        />
      </>
    ),
    sidePhoto: (
      <>
        {/* Mobile photo */}
        <LazyImage
          src={catToy2x}
          display={['block', 'none']}
          alt="side photo Wag! ball"
          width={['80px', null, '220px']}
          height={['210px', null, '220px']}
          position="absolute"
          right={['-16px', 'unset']}
          top={['-72px', null, '-240px']}
          imageProps={{
            style: {
              objectFit: 'contain',
            },
          }}
        />
        {/* Desktop photo */}
        <LazyImage
          src={catToy3x}
          display={['none', 'block']}
          alt="side photo Wag! ball"
          width={['80px', null, '220px']}
          height={['210px', null, '220px']}
          position="absolute"
          left={['unset', '-34px']}
          top={['-72px', null, '-240px']}
          imageProps={{
            style: {
              objectFit: 'contain',
            },
          }}
        />
      </>
    ),
    IconComponent: () => null,
    url: ServiceUrls.CatFoodAdvisor,
    src: cfa2xImage,
    srcSet: `${cfa2xImage} 2x`,
    description: (
      <Typography>
        Search and browse the best food for your cat based on unbiased,
        science-backed reviews and ratings.
      </Typography>
    ),
  },
];

export const ServicesSection = () => (
  <Box
    overflow="hidden"
    px="unset"
  >
    <Box
      mb={[7, null, 10]}
    >
      <Typography
        color="#474747"
        mb={0}
        textAlign="center"
        variant="h2"
      >
        Wag! Platform
      </Typography>
      <Typography
        fontWeight="normal"
        my={[1, null, 3]}
        textAlign="center"
        variant="h3"
      >
        Everything a happy pet needs
      </Typography>
    </Box>
    {services.map(({
      url,
      IconComponent,
      name,
      nameBlock,
      description,
      src,
      srcSet,
      sidePhoto,
      responsive,
    }, i) => (
      <Flex
        key={name}
        mb={['32px', null, '130px']}
        flexDirection={['column', null, 'row']}
        position="relative"
        px={['16px', 7]}
      >
        <Flex
          flex={2}
          order={isEven(i) ? [2, null, 1] : 2}
          justifyContent={['flex-start', null, isEven(i) ? 'flex-end' : 'flex-start']}
        >
          <Box
            data-aos="fade-in"
            data-aos-easing="ease-in-out"
            data-aos-duration="500"
          >
            {sidePhoto}
          </Box>
          {url ? (
            <Link
              href={url}
              target="_blank"
              aria-label={name}
              display={['block', null, 'block']}
              width={['100%', null, 'auto']}
              mt={['0px', null, 0]}
            >
              {responsive ? (
                <>
                  <LazyImage
                    display={['block', 'none']}
                    srcSet={responsive.mobile.srcSet}
                    src={responsive.mobile.src}
                    imageProps={{ borderRadius: 3 }}
                    alt={`${name} service`}
                    height={['320px', null, '500px']}
                    width={[1, null, '700px']}
                    position="relative"
                    zIndex={1}
                  />
                  <LazyImage
                    display={['none', 'block']}
                    srcSet={responsive.desktop.srcSet}
                    src={responsive.desktop.src}
                    imageProps={{ borderRadius: 3 }}
                    alt={`${name} service`}
                    height={['320px', null, '500px']}
                    width={[1, null, '700px']}
                    position="relative"
                    zIndex={1}
                  />
                </>
              ) : (
                <LazyImage
                  srcSet={srcSet}
                  src={src}
                  imageProps={{ borderRadius: 3 }}
                  alt={`${name} service`}
                  height={['320px', null, '500px']}
                  width={[1, null, '700px']}
                  position="relative"
                  zIndex={1}
                />
              )}
            </Link>
          ) : (
            <>
              {responsive ? (
                <>
                  <LazyImage
                    display={['block', 'none']}
                    srcSet={responsive.mobile.srcSet}
                    src={responsive.mobile.src}
                    imageProps={{ borderRadius: 3 }}
                    alt={`${name} service`}
                    height={['320px', null, '500px']}
                    width={[1, '700px', '700px']}
                    position="relative"
                    zIndex={1}
                    mt={['0px', null, 0]}
                  />
                  <LazyImage
                    display={['none', 'block']}
                    srcSet={responsive.desktop.srcSet}
                    src={responsive.desktop.src}
                    imageProps={{ borderRadius: 3 }}
                    alt={`${name} service`}
                    height={['320px', null, '500px']}
                    width={[1, '700px', '700px']}
                    position="relative"
                    zIndex={1}
                    mt={['0px', null, 0]}
                  />
                </>
              ) : (
                <LazyImage
                  srcSet={srcSet}
                  src={src}
                  imageProps={{ borderRadius: 3 }}
                  alt={`${name} service`}
                  height={['320px', null, '500px']}
                  width={[1, '700px', '700px']}
                  position="relative"
                  zIndex={1}
                  mt={['0px', null, 0]}
                />
              )}
            </>
          )}
        </Flex>
        <Flex
          order={isEven(i) ? [1, null, 2] : 1}
          alignItems={['flex-start', null, isEven(i) ? 'flex-start' : 'flex-end']}
          flex={1}
          flexDirection="column"
          justifyContent="center"
          mb={[1, null, 0]}
          position="relative"
          px={[2, null, 4]}
          textAlign={['left', null, isEven(i) ? 'left' : 'right']}
        >
          <Box
            data-aos="fade-in"
            data-aos-easing="ease-in-out"
            data-aos-duration="500"
          >
            {url ? (
              <Link
                href={url}
                target="_blank"
                aria-label={name}
              >
                <IconComponent
                  position={['relative', null, 'absolute']}
                  mb={[2, null, 0]}
                  ml={[0, null, isEven(i) ? '-30px' : 0]}
                  mr={[0, null, isEven(i) ? 0 : '-30px']}
                  left={[0, null, isEven(i) ? 0 : 'auto']}
                  right={['auto', null, isEven(i) ? 'auto' : 0]}
                  top={[0, null, '60px']}
                  height={['56px', null, '110px']}
                  width={['56px', null, '111px']}
                  zIndex={1}
                />
              </Link>
            ) : (
              <IconComponent
                position={['relative', null, 'absolute']}
                mb={[2, null, 0]}
                ml={[0, null, isEven(i) ? '-30px' : 0]}
                mr={[0, null, isEven(i) ? 0 : '-30px']}
                left={[0, null, isEven(i) ? 0 : 'auto']}
                right={['auto', null, isEven(i) ? 'auto' : 0]}
                top={[0, null, '60px']}
                height={['56px', null, '110px']}
                width={['56px', null, '111px']}
                zIndex={1}
              />
            )}
          </Box>

          <Typography
            variant="h3"
            fontFamily="muliBold"
            fontSize={['18px', null, '24px']}
            m={0}
          >
            {(() => {
              const content = nameBlock ? (
                <Box
                  as="span"
                  display="inline-block"
                  fontSize={0}
                  lineHeight={0}
                >
                  {nameBlock}
                </Box>
              ) : (
                <span>{name}</span>
              );
              return url ? (
                <Link
                  href={url}
                  color="wagGreen"
                  textTransform="uppercase"
                  target="_blank"
                  aria-label={name}
                >
                  {content}
                </Link>
              ) : (
                content
              );
            })()}
          </Typography>
          <Box
            maxWidth={['100%', null, '340px']}
          >
            {description}
          </Box>
        </Flex>
      </Flex>
    ))}
  </Box>
);
